import { UserData, UserCompany } from "../Users";
import { TableQueryFilter } from "../common";
import { ChildEntityHydrationParams } from "../Utils";
import { AssessmentRepresentationStyle, DocumentRecipient } from "../Documents";
import { Block } from "../ProposalBuilder/Block";
import { QbrHealthStatusEnum } from "../QBR";

export class AddAssessmentRequest {
	name = "";
	opportunity_id: string | null = null;
	client_company_id: string | null = null;
	tax_region_id: string | null = null;
	logs_url?: string;
	recipients: DocumentRecipient[] = new Array<DocumentRecipient>();
	assessment_data_logs: AssessmentDataLogRequestSubObj[] =
		new Array<AssessmentDataLogRequestSubObj>();
}

export class AssessmentsRequest {
	filters: TableQueryFilter[];
	keywords: string | null;
	includeEntities: ChildEntityHydrationParams[] | undefined;
	constructor(
		filters: TableQueryFilter[],
		keywords: string | null,
		includeEntities?: ChildEntityHydrationParams[]
	) {
		this.filters = filters;
		this.keywords = keywords;
		this.includeEntities = includeEntities;
	}
}

export enum AssessmentDataType {
	InfraAssetInfo = "infra_asset_info",
	Vulnerability = "vulnerability"
}

export class InfraAssessmentData {
	ipv4_address = "";
	operating_system = "";
	operating_system_version = "";
	dns_host_name = "";
	data = "";
}

export type AssessmentData = InfraAssessmentData | AssessmentVulnerabilityData;

export class AssessmentDataLog {
	id = "";
	created: Date = new Date();
	updated: Date = new Date();
	type: AssessmentDataType | null = null;
	company_id = "";
	data: AssessmentData | null = null;
	hide = false;
	risk_matrix_cell_data?: RiskMatrixCellData[];
	risk_bar_score?: number;
	risk_listing_data?: AssessmentVulnerabilityData[];
}

export class EditAssessmentDataLogRequest {
	hide?: boolean;
	data?: AssessmentData;
	id = "";
}

export class AssessmentVulnerabilityDataLog {
	id = "";
	created: Date = new Date();
	updated: Date = new Date();
	company_id = "";
	type: AssessmentDataType | null = null;
	data: AssessmentVulnerabilityData | null = null;
	hide = false;
}

export interface SeverityLabel {
	description: string;
	label: string;
	severity: string;
}

export interface LikelihoodLabel {
	description: string;
	label: string;
	likelihood: string;
}

export interface LegendLabel {
	label: string;
	legend_risk_value: AssessmentRiskLevel;
	color: string;
}

export class AssessmentCategory {
	assessment_data_list: string[] = new Array<string>();
	cloud_assessment_type?: CloudAssessmentType;
	issue_id = "";
	issue_type = "";
	manual_assessment_data_list: string[] = new Array<string>();
	filter_on_status: IssueStatusEnum | null = null;
}

export enum AssessmentRiskLevel {
	HIGH = "high_risk",
	MEDIUM = "medium_risk",
	LOW = "low_risk"
}

export class Assessment {
	assessment_logs: { data: AssessmentDataLog[] } = {
		data: new Array<AssessmentDataLog>()
	};
	company: UserCompany | null = null;
	company_id = "";
	created: Date = new Date();
	document_id = "";
	id = "";
	issue_types_list: AssessmentCategory[] = new Array<AssessmentCategory>();
	legend_labels: LegendLabel[] = [];
	likelihood_labels: LikelihoodLabel[] = [];
	likelihood_header_label = "";
	manual_assessment_logs: { data: AssessmentDataLog[] } = {
		data: new Array<AssessmentDataLog>()
	};
	name = "";
	opportunity_id?: string;
	recipients: DocumentRecipient[] = [];
	representation_style: AssessmentRepresentationStyle =
		AssessmentRepresentationStyle.SEVERITY_LIKELIHOOD;
	risk_matrix_cell_data?: RiskMatrixCellData[];
	risk_bar_score?: number;
	risk_listing_data?: AssessmentVulnerabilityData[];
	severity_header_label = "";
	severity_labels: SeverityLabel[] = [];
	updated: Date = new Date();
	user_id = "";
	user: UserData | null = null;
}

export class AssessmentEditRequest {
	severity_labels: SeverityLabel[] = [];
	legend_labels: LegendLabel[] = [];
	severity_header_label = "";
	likelihood_header_label = "";
	risk_bar_score?: number;
	likelihood_labels: LikelihoodLabel[] = [];
}

export enum AssessmentSeverity {
	INTOLERABLE = "intolerable",
	TOLERABLE = "tolerable",
	ACCEPTABLE = "acceptable"
}

export enum AssessmentLikelihood {
	HIGHLY_LIKELY = "highly_likely",
	POSSIBLE = "possible",
	UNLIKELY = "unlikely"
}

export enum IssueStatusEnum {
	OPEN = "open",
	RESOLVED = "resolved",
	ALL = "Show all Issues"
}
export class AssessmentVulnerabilityData {
	cloud_assessment_type?: CloudAssessmentType | null = null;
	issue_type = "";
	issue_sub_type = "";
	summary = "";
	severity: AssessmentSeverity = AssessmentSeverity.ACCEPTABLE;
	likelihood: AssessmentLikelihood = AssessmentLikelihood.UNLIKELY;
	recommendation = "";
	status: IssueStatusEnum = IssueStatusEnum.OPEN;
}

export class AssessmentDataLogRequestSubObj {
	type: AssessmentDataType = AssessmentDataType.Vulnerability;
	data: AssessmentData = new AssessmentVulnerabilityData();
}

export class AssessmentsResponse {
	data: Assessment[] = new Array<Assessment>();
}

export const AssessmentDataTypeLabelMapper = {
	Computers: AssessmentDataType.InfraAssetInfo,
	"Web Servers": null,
	Printers: null,
	"Exchange Servers": null,
	"MS SQL Servers": null,
	"MX Records": null
};

export interface AssessmentVulnerabilityDataLogFormValues {
	issue_sub_type: string;
	issue_type: string;
	severity: AssessmentSeverity;
	likelihood: AssessmentLikelihood;
	summary?: string;
	recommendation?: string;
}

export interface RiskMatrixCellData {
	severity: AssessmentSeverity;
	likelihood: AssessmentLikelihood;
	count: number;
}

export interface AssessmentDetailModalProps {
	currentActiveAssessment: Assessment | null;
	visible: boolean;
	onCancel: (e: React.MouseEvent<HTMLElement>) => void;
	onOk: (e: React.MouseEvent<HTMLElement>) => void;
}

export interface CreateCategoryRequest {
	entityId: string;
	issueType: string | null;
	cloud_assessment_type?: CloudAssessmentType;
}

export interface ModifyCategoryRequest extends CreateCategoryRequest {
	issueId: string;
	filterOnStatus: IssueStatusEnum | null;
}

export interface ReorderCategoriesRequest {
	entityId: string;
	newCategoryList: string[];
}

export enum CloudAssessmentType {
	Ms365 = "ms365"
}

export class Ms365Assessment {
	block?: Block;
	company_id = "";
	created: Date = new Date();
	id = "";
	name = "";
	updated: Date = new Date();
	user_id = "";
	user: UserData | null = null;
}

export interface AssessmentIssue {
	id: string;
	category: string;
	issueName: string;
	hide: boolean;
	created: Date;
	updated: Date;
	type: AssessmentDataType | string | null;
	companyId: string;
	summary?: string;
	recommendation?: string;
	severity?: AssessmentSeverity | null;
	likelihood?: AssessmentLikelihood | null;
	riskLevel?: AssessmentRiskLevel;
	budget?: number;
	health?: QbrHealthStatusEnum | null;
	quarter?: string;
	risk?: string;
	solution?: string;
	targetYear?: string;
	possibleRiskValues?: string[] | null;
	possibleStatusValues?: string[] | null;
	possibleSolutionValues?: string[] | null;
	cloudAssessmentType?: CloudAssessmentType | null;
	status: IssueStatusEnum;
}
